'use client'
import { useTranslation } from '@/app/i18n/client'
import { solutionPlans } from '@/app/utils/pricingData'
import Image from 'next/image'
import SubscriptionButton from './SubscriptionButton'
import { SubscriptionType } from '@/app/types/subscriptionType'

interface TableHeadRowProps {
  plan: string
  subscriptionsList: SubscriptionType[]
}
export const TableHeadRow: React.FC<TableHeadRowProps> = ({
  plan,
  subscriptionsList,
}) => {
  const { t } = useTranslation()
  const findPrice = (plan: string) =>
    subscriptionsList?.find((sub) => plan.includes(sub?.name))
  const selectedPlanIndex = solutionPlans.findIndex((p) => p.name === plan)
  return (
    <tr className="borderBottom">
      <th className="text-left pl-[50px] mobile:pl-[41px] w-[328px]">
        <p className="text-2xl font-ns_semibold">{t('Plans')}</p>
      </th>
      {solutionPlans.map((plan, index) => (
        <th
          key={index}
          className={`${plan.bgColor} w-[${plan.width}px] ${index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
        >
          <p className="text-[#df7d2f] text-[22px]">{t(plan.name)}</p>

          <div className="flex flex-col justify-between h-40 mobile:h-32">
            <p className="mt-4 lowercase">
              {findPrice(plan.name) && plan.name !== 'Free Member' && (
                <>
                  {Number(findPrice(plan.name)?.price).toString()}
                  {'€ / '}
                  {t('Year')}
                </>
              )}
            </p>
            {plan.plan === 'Silver' && (
              <p className="text-base text-[#888888] mt-[5px] px-3 font-ns_semibold">
                + {t('delivery cost of the Directories')}
              </p>
            )}
            <div>
              <SubscriptionButton subscriptionType={plan.plan} />
            </div>
          </div>
        </th>
      ))}
    </tr>
  )
}

interface TableCellProps {
  text?: string
  isImageCell?: boolean
  textColor?: string
  columnIndex: number
  textSize?: string
  className: string
}

const TableCell = ({
  text,
  isImageCell,
  columnIndex = 0,
  textColor = 'text-[#000000]',
  className,
}: TableCellProps) => {
  const bgColors = ['#FFFFFF', '#fffbe3', '#fbffe5', '#fffbe3', '#fbffe5']
  const { t } = useTranslation()

  return (
    <td
      className={`bg-[${bgColors[columnIndex]}] ${textColor} ${className} pt-[22px] pl-[17px] pb-[16px] text-sm ${columnIndex === 0 ? 'font-ns_semibold !text-base !text-[#888888]' : 'text-center px-6'}`}
    >
      {isImageCell && (
        <div className="flex items-center justify-center">
          <Image
            loading="lazy"
            width={16}
            height={12}
            src="/assets/Icons/tickIcon.svg"
            alt=""
          />
        </div>
      )}
      {text && t(text ?? '')}
    </td>
  )
}

export default TableCell

interface tableSubHeaderProps {
  title: string
  plan: string
}
export const TableSubHeader = ({ title, plan }: tableSubHeaderProps) => {
  const selectedPlanIndex = solutionPlans.findIndex((p) => p.name === plan)

  return (
    <tr className="grey-border">
      <td className="text-sm pl-[17px] font-ns_bold text-bright_orange">
        {title}
      </td>
      {[...Array(4)].map((_, index) => (
        <td
          key={index + Math.random()}
          className={`${index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
        ></td>
      ))}
    </tr>
  )
}

export interface TableSubHeaderWithDataProps {
  title: string
  data: { text?: string; isImageCell?: boolean }[]
  plan: string
}

export const TableSubHeaderWithData = ({
  title,
  data,
  plan,
}: TableSubHeaderWithDataProps) => {
  const selectedPlanIndex = solutionPlans.findIndex((p) => p.name === plan)
  const { t } = useTranslation()
  return (
    <tr className="grey-border">
      <td className="text-sm pl-[17px] font-ns_bold text-bright_orange uppercase !py-3">
        {title}
      </td>
      {data?.map((cell, index) => (
        <td
          key={`${index}-${cell}`}
          className={`text-sm text-center ${index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
        >
          <div className="flex gap-2 justify-center">
            {cell.isImageCell && (
              <div className="flex items-center justify-center">
                <Image
                  loading="lazy"
                  width={16}
                  height={12}
                  src="/assets/Icons/tickIcon.svg"
                  alt=""
                />
              </div>
            )}
            {cell.text && t(cell.text)}
          </div>
        </td>
      ))}
    </tr>
  )
}

export const EmptyTableRow = ({ plan }: { plan: string }) => {
  const selectedPlanIndex = solutionPlans.findIndex((p) => p.name === plan)

  return (
    <tr className="borderBottom">
      <td></td>
      {[...Array(4)].map((_, index) => (
        <td
          key={index + Math.random()}
          className={`bg-[#fbffe5] ${index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
        ></td>
      ))}
    </tr>
  )
}
